import React, { useMemo } from 'react';
import './style.less';
import { Empty, Skeleton } from 'antd';
import { formatCurrency } from '../../utils/format';
import ReactApexChart from 'react-apexcharts';

/**
 * Брэндийн хөх (дүн) ба ногоон (тоо хэмжээ) — цайвар өнгө нь цагаан дэвсгэр
 * дээр бүдгэрдэг тул график дээр ханалтыг нь чангалж, бага зэрэг гүнзгийрүүлэв
 * (брэндийн #2FA1D9 / #2ABB7F-ийн тод хувилбар).
 */
const AMOUNT_COLOR = '#1789CD';
const UNIT_COLOR = '#12A96A';

const AMOUNT_NAME = 'Нийт дүн';
const UNIT_NAME = 'Тоо хэмжээ';

/**
 * Тэнхлэгийн шошгонд бүтэн дүн ("5,051,900.00₮") багтахгүй тул товчилно.
 * Tooltip дотор бүтэн дүнг нь харуулна.
 */
const compactAmount = (value) => {
  const amount = Number(value) || 0;
  if (Math.abs(amount) >= 1e6) return `${(amount / 1e6).toFixed(1)}сая`;
  if (Math.abs(amount) >= 1e3) return `${Math.round(amount / 1e3)}мян`;
  return `${amount}`;
};

/** "2026-08-11" → "08/11". Таних боломжгүй бол хэвээр нь үлдээнэ. */
const shortDate = (value) => {
  const parts = String(value ?? '').split('-');
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : String(value ?? '');
};

const SalesChart = ({ dailyChart, loading }) => {
  const { saleDate = [], salesAmount = [], salesUnit = [] } = dailyChart ?? {};
  const isEmpty = !loading && saleDate.length === 0;

  const series = useMemo(
    () => [
      { name: AMOUNT_NAME, data: salesAmount },
      { name: UNIT_NAME, data: salesUnit },
    ],
    [salesAmount, salesUnit]
  );

  const options = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      colors: [AMOUNT_COLOR, UNIT_COLOR],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 3 },
      fill: {
        type: 'gradient',
        gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 95] },
      },
      /**
       * Дүн (сая ₮) ба тоо хэмжээ (хэдэн арав) хэмжээсийн зөрүү маш их тул
       * нэг тэнхлэг дээр тоо хэмжээ ёроолд хавтгай зураас болж харагддаг.
       * Тус бүрд нь тусдаа тэнхлэг өгнө.
       */
      yaxis: [
        {
          seriesName: AMOUNT_NAME,
          labels: { formatter: compactAmount, style: { colors: AMOUNT_COLOR } },
          title: { text: 'Дүн (₮)', style: { color: AMOUNT_COLOR, fontWeight: 500 } },
        },
        {
          seriesName: UNIT_NAME,
          opposite: true,
          labels: {
            formatter: (value) => `${Math.round(Number(value) || 0)}`,
            style: { colors: UNIT_COLOR },
          },
          title: { text: 'Тоо хэмжээ', style: { color: UNIT_COLOR, fontWeight: 500 } },
        },
      ],
      xaxis: {
        categories: saleDate.map(shortDate),
        // 30 хоногийн бүх огноо багтахгүй тул Apex өөрөө сийрэгжүүлнэ
        tickAmount: Math.min(saleDate.length, 8),
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      grid: { borderColor: '#f1f2f4', strokeDashArray: 4 },
      legend: { position: 'top', horizontalAlign: 'right', markers: { radius: 4 } },
      tooltip: {
        shared: true,
        intersect: false,
        style: { fontSize: '13px' },
        // Бүтэн огноог tooltip-д нь харуулж, тэнхлэг дээрх товчлолыг нөхнө
        x: { formatter: (_value, { dataPointIndex }) => saleDate[dataPointIndex] ?? '-' },
        y: [
          { formatter: (value) => formatCurrency(value ?? 0) },
          { formatter: (value) => `${Math.round(Number(value) || 0)}ш` },
        ],
      },
    }),
    [saleDate]
  );

  return (
    <div className="db_item_container sales_chart_container">
      <div className="db_item_container_header">
        <h3>Борлуулалтын хэмжээ</h3>
      </div>
      <div className="db_item_container_body">
        {loading && <Skeleton active paragraph={{ rows: 6 }} />}
        {isEmpty && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Сонгосон хугацаанд борлуулалт бүртгэгдээгүй"
          />
        )}
        {!loading && !isEmpty && (
          <ReactApexChart options={options} series={series} type="area" height={300} />
        )}
      </div>
    </div>
  );
};

export default SalesChart;
